/**
 * @internal true
 * @module briefing/BriefingAgenda
 * Widget de agenda do dia — mini timeline com eventos do dia.
 * Reutiliza dados de AgendaConsolidada (endpoint /agenda/consolidada).
 * Clique no evento → navega para /admin/agenda ou expande detalhes.
 */
import { CalendarDays, ChevronRight, Award, BookOpen, Bell, Megaphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { EventoConsolidado } from './briefing.types';

interface BriefingAgendaProps {
  eventos: EventoConsolidado[];
  loading: boolean;
}

function getEventIcon(e: EventoConsolidado) {
  if (e.origem === 'AVALIACAO') {
    return e.tipo === 'PROVA' ? Award : BookOpen;
  }
  if (e.tipo === 'INSTITUCIONAL' || e.tipo === 'COMUNICADO') return Megaphone;
  return Bell;
}

function formatHora(isoDate: string): string {
  try {
    const d = new Date(isoDate);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '--:--';
  }
}

export function BriefingAgenda({ eventos, loading }: BriefingAgendaProps) {
  const navigate = useNavigate();

  return (
    <div className="brief-card">
      <div className="brief-card-head">
        <h3 className="brief-card-title">
          <CalendarDays style={{ color: '#3b82f6' }} />
          Agenda do Dia
          {!loading && eventos.length > 0 && (
            <span className="brief-card-badge brief-card-badge--blue">{eventos.length}</span>
          )}
        </h3>
        <button
          className="brief-card-link"
          onClick={() => navigate('/admin/agenda')}
        >
          Ver agenda completa <ChevronRight size={12} />
        </button>
      </div>
      <div className="brief-card-body">
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="brief-skeleton" style={{ height: 44, borderRadius: 10 }} />
            ))}
          </div>
        ) : eventos.length === 0 ? (
          <div className="brief-empty">
            <CalendarDays size={28} />
            <span className="brief-empty-text">Nenhum evento agendado para hoje.</span>
          </div>
        ) : (
          <div className="brief-agenda-timeline">
            {eventos.slice(0, 10).map((evt) => {
              const EvtIcon = getEventIcon(evt);
              return (
                <div
                  key={evt.id}
                  className="brief-agenda-event"
                  style={{ borderLeftColor: evt.corTema || '#3b82f6' }}
                  onClick={() => navigate('/admin/agenda')}
                >
                  <span className="brief-agenda-time">{formatHora(evt.data)}</span>
                  <div className="brief-agenda-info">
                    <p className="brief-agenda-info-title">{evt.titulo}</p>
                    <p className="brief-agenda-info-sub">
                      {evt.origem === 'AVALIACAO' ? '📝 Avaliação' : '📢 Comunicado'}
                      {evt.turmaNome && <> · {evt.turmaNome}</>}
                      {evt.materiaNome && <> · {evt.materiaNome}</>}
                    </p>
                  </div>
                  <div style={{
                    width: 28, height: 28, borderRadius: 7,
                    background: `${evt.corTema || '#3b82f6'}12`,
                    color: evt.corTema || '#3b82f6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <EvtIcon size={14} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
